import React, { useState } from "react";

interface LaReq { id: string; council: string; title: string; dueDate: string; status: string; evidence: string; }
interface CqcTheme { id: string; title: string; receivedDate: string; responseBy: string; status: string; response: string; }
interface Lado { id: string; date: string; allegation: string; referredWithin1Day: boolean; ladoRef: string; outcome: string; }

const LA_KEY = "scrutiny_la";
const CQC_KEY = "scrutiny_cqc";
const LADO_KEY = "scrutiny_lado";

const COUNCILS = ["Wakefield", "Leeds", "Kirklees", "Bradford"];
const STATUSES = ["Open", "In progress", "Submitted", "Closed"];

const DEFAULT_LA: LaReq[] = [
  { id: "la-1", council: "Wakefield", title: "Safeguarding Adults Board annual return", dueDate: "", status: "Open", evidence: "" },
  { id: "la-2", council: "Wakefield", title: "Quality monitoring visit evidence pack", dueDate: "", status: "Open", evidence: "" },
  { id: "la-3", council: "Wakefield", title: "Contract monitoring return (quarterly)", dueDate: "", status: "Open", evidence: "" },
  { id: "la-4", council: "Leeds", title: "Making Safeguarding Personal - voice recorded evidence", dueDate: "", status: "Open", evidence: "" },
  { id: "la-5", council: "Kirklees", title: "Gateway team notification & panel review minutes", dueDate: "", status: "Open", evidence: "" },
  { id: "la-6", council: "Bradford", title: "Placement agreement compliance return", dueDate: "", status: "Open", evidence: "" },
];

const load = <T,>(key: string, fallback: T[]): T[] => {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw) as T[];
  } catch {}
  return fallback;
};
const saveKey = (key: string, data: any[]) => localStorage.setItem(key, JSON.stringify(data));
const today = () => new Date().toISOString().slice(0, 10);
const daysTo = (d: string) => Math.ceil((new Date(d).getTime() - Date.now()) / 86400000);

export default function Scrutiny({ user }: { user?: any }) {
  const [tab, setTab] = useState<"la" | "cqc" | "lado">("la");
  const [la, setLa] = useState<LaReq[]>(() => load(LA_KEY, DEFAULT_LA));
  const [cqc, setCqc] = useState<CqcTheme[]>(() => load(CQC_KEY, []));
  const [lado, setLado] = useState<Lado[]>(() => load(LADO_KEY, []));
  const [message, setMessage] = useState("");

  const [laForm, setLaForm] = useState<Partial<LaReq>>({ council: "Wakefield", status: "Open" });
  const [cqcForm, setCqcForm] = useState<Partial<CqcTheme>>({ receivedDate: today(), status: "Open" });
  const [ladoForm, setLadoForm] = useState<Partial<Lado>>({ date: today(), referredWithin1Day: true });

  const say = (m: string) => { setMessage(m); setTimeout(() => setMessage(""), 4000); };

  const addLa = () => {
    if (!laForm.title) { say("Enter the requirement title."); return; }
    const next = [{ id: `la-${Date.now()}`, council: laForm.council || "Wakefield", title: laForm.title || "", dueDate: laForm.dueDate || "", status: laForm.status || "Open", evidence: laForm.evidence || "" }, ...la];
    setLa(next); saveKey(LA_KEY, next); setLaForm({ council: "Wakefield", status: "Open" });
    say("Local authority requirement added.");
  };

  const updateLa = (id: string, patch: Partial<LaReq>) => { const next = la.map(l => (l.id === id ? { ...l, ...patch } : l)); setLa(next); saveKey(LA_KEY, next); };

  const addCqc = () => {
    if (!cqcForm.title) { say("Enter the theme / information request."); return; }
    const next = [{ id: `cqc-${Date.now()}`, title: cqcForm.title || "", receivedDate: cqcForm.receivedDate || today(), responseBy: cqcForm.responseBy || "", status: cqcForm.status || "Open", response: cqcForm.response || "" }, ...cqc];
    setCqc(next); saveKey(CQC_KEY, next); setCqcForm({ receivedDate: today(), status: "Open" });
    say("CQC theme / information request logged.");
  };

  const updateCqc = (id: string, patch: Partial<CqcTheme>) => { const next = cqc.map(c => (c.id === id ? { ...c, ...patch } : c)); setCqc(next); saveKey(CQC_KEY, next); };

  const addLado = () => {
    if (!ladoForm.allegation) { say("Describe the allegation."); return; }
    const next = [{ id: `lado-${Date.now()}`, date: ladoForm.date || today(), allegation: ladoForm.allegation || "", referredWithin1Day: !!ladoForm.referredWithin1Day, ladoRef: ladoForm.ladoRef || "", outcome: ladoForm.outcome || "" }, ...lado];
    setLado(next); saveKey(LADO_KEY, next); setLadoForm({ date: today(), referredWithin1Day: true });
    say("LADO referral logged.");
  };

  const laOverdue = la.filter(l => l.dueDate && daysTo(l.dueDate) < 0 && l.status !== "Closed" && l.status !== "Submitted");
  const cqcOverdue = cqc.filter(c => c.responseBy && daysTo(c.responseBy) < 0 && c.status !== "Closed" && c.status !== "Submitted");

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Local Authority & External Scrutiny Hub</h2>
        <p className="text-sm text-slate-600">Every council requirement, CQC theme / information request and LADO referral tracked with deadlines - zero missed statutory responses.</p>
      </div>

      {laOverdue.length || cqcOverdue.length ? (
        <div className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">OVERDUE: {laOverdue.length} local authority requirement(s) and {cqcOverdue.length} CQC response(s). Escalate to the Registered Manager now.</div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {(["la", "cqc", "lado"] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={tab === t ? "rounded-lg bg-blue-900 px-3 py-2 text-sm font-medium text-white" : "rounded-lg border border-slate-300 px-3 py-2 text-sm"}>{t === "la" ? "LA Requirements" : t === "cqc" ? "CQC Themes & Requests" : "LADO Log"}</button>
        ))}
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {tab === "la" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">Add a council requirement</h3>
            <div className="grid gap-3 md:grid-cols-4">
              <select value={laForm.council || "Wakefield"} onChange={e => setLaForm(f => ({ ...f, council: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
                {COUNCILS.map(c => <option key={c}>{c}</option>)}
              </select>
              <input value={laForm.title || ""} onChange={e => setLaForm(f => ({ ...f, title: e.target.value }))} placeholder="Requirement" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input type="date" value={laForm.dueDate || ""} onChange={e => setLaForm(f => ({ ...f, dueDate: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input value={laForm.evidence || ""} onChange={e => setLaForm(f => ({ ...f, evidence: e.target.value }))} placeholder="Evidence / where filed" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <button onClick={addLa} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Add requirement</button>
          </div>
          <div className="space-y-2">
            {la.map(l => (
              <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-medium text-slate-900">{l.council} - {l.title}</div>
                  <div className="flex items-center gap-2">
                    {l.dueDate ? <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${daysTo(l.dueDate) < 0 && l.status !== "Closed" && l.status !== "Submitted" ? "bg-red-100 text-red-700" : daysTo(l.dueDate) <= 14 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{daysTo(l.dueDate) < 0 ? "Overdue" : `Due in ${daysTo(l.dueDate)}d`}</span> : null}
                    <select value={l.status} onChange={e => updateLa(l.id, { status: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">
                      {STATUSES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-1 text-xs text-slate-600">{l.evidence || "No evidence note yet"}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {tab === "cqc" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">Log a CQC theme / information request</h3>
            <div className="grid gap-3 md:grid-cols-3">
              <input value={cqcForm.title || ""} onChange={e => setCqcForm(f => ({ ...f, title: e.target.value }))} placeholder="Theme or request (e.g. infection control information request)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input type="date" value={cqcForm.receivedDate || ""} onChange={e => setCqcForm(f => ({ ...f, receivedDate: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input type="date" value={cqcForm.responseBy || ""} onChange={e => setCqcForm(f => ({ ...f, responseBy: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <button onClick={addCqc} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Log request</button>
          </div>
          <div className="space-y-2">
            {cqc.map(c => (
              <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-medium text-slate-900">{c.title}</div>
                  <div className="flex items-center gap-2">
                    {c.responseBy ? <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${daysTo(c.responseBy) < 0 && c.status !== "Closed" && c.status !== "Submitted" ? "bg-red-100 text-red-700" : daysTo(c.responseBy) <= 7 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{daysTo(c.responseBy) < 0 ? "Overdue" : `Respond by ${c.responseBy}`}</span> : null}
                    <select value={c.status} onChange={e => updateCqc(c.id, { status: e.target.value })} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">
                      {STATUSES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div className="mt-1 text-xs text-slate-600">Received {c.receivedDate} | Response: {c.response || "not yet recorded"}</div>
              </div>
            ))}
            {cqc.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No CQC themes or information requests logged.</div> : null}
          </div>
        </div>
      ) : null}

      {tab === "lado" ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 font-semibold text-slate-900">Log a LADO referral (1-working-day rule)</h3>
            <div className="grid gap-3 md:grid-cols-3">
              <input type="date" value={ladoForm.date || ""} onChange={e => setLadoForm(f => ({ ...f, date: e.target.value }))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input value={ladoForm.allegation || ""} onChange={e => setLadoForm(f => ({ ...f, allegation: e.target.value }))} placeholder="Allegation (confidential)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <input value={ladoForm.ladoRef || ""} onChange={e => setLadoForm(f => ({ ...f, ladoRef: e.target.value }))} placeholder="LADO reference" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!ladoForm.referredWithin1Day} onChange={e => setLadoForm(f => ({ ...f, referredWithin1Day: e.target.checked }))} />Referred to LADO within 1 working day</label>
              <input value={ladoForm.outcome || ""} onChange={e => setLadoForm(f => ({ ...f, outcome: e.target.value }))} placeholder="Outcome" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            </div>
            <button onClick={addLado} className="mt-3 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Log referral</button>
          </div>
          <div className="space-y-2">
            {lado.map(l => (
              <div key={l.id} className={`rounded-xl border p-3 shadow-sm ${l.referredWithin1Day ? "border-slate-200 bg-white" : "border-red-300 bg-red-50"}`}>
                <div className="text-sm text-slate-800">{l.date} | {l.allegation}</div>
                <div className="mt-1 text-xs text-slate-600">{l.referredWithin1Day ? "Referred within 1 working day" : "NOT referred within 1 working day - review immediately"} | LADO ref {l.ladoRef || "-"} | Outcome {l.outcome || "pending"}</div>
              </div>
            ))}
            {lado.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No LADO referrals logged.</div> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}